import * as React from "react";

import { useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import CardHeader from "@material-ui/core/CardHeader";
import Alert from "@material-ui/lab/Alert";
import IconButton from "@material-ui/core/IconButton";
import CheckIcon from "@material-ui/icons/Check";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { Card, Divider } from "@material-ui/core";

import { Button, PageHeader, translate, showNotification } from "jazasoft";

import MuiTable from "../../../components/MuiTable";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState, useSetting, useTime, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { OState, ReturnStatus, Role, RollReturnTxnType } from "../../../utils/types";
import { isEmpty, toFixed, toFixedValue } from "../../../utils/helpers";
import { formatDateTime } from "../../../utils/dateUtils";
import { buildTransactionRows, isKnitRoll } from "../../../helpers/rollReturnHelper";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { colorLightGreen, colorSecondary, colorTomatoRed } from "../../../utils/colors";

import { getCutOrderRows } from "../../cutplan/cutPlanHelper";

import RollReturnDrawer from "./RollReturnDrawer";
import RollReturnConfirmDrawer from "./RollReturnConfirmDrawer";
import RollReturnDetailsDrawer from "./RollReturnDetailsDrawer";
import OrderCloseDrawer from "./OrderCloseDrawer";

const ALERT_PARAM_TITLE = {
  fabricLeftover: "Fabric Leftover",
  higherRecut: "High Recutting",
  higherEndBits: "High End Bits"
};

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  footer: {
    padding: "1.5em",
    display: "flex",
    justifyContent: "center"
  },
  tableTitle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6
  },
  tableTitleInfo: {
    color: "#757575",
    fontSize: 18
  },
  rollDefinitionTooltip: {
    fontSize: 10
  },
  remarksHeader: {
    padding: "14px 20px 12px"
  },
  remarksTitle: {
    fontSize: 16,
    fontWeight: 500
  },
  remarksContent: {
    padding: "0 20px"
  },
  remarkRow: {
    display: "grid",
    gridTemplateColumns: "minmax(140px, 220px) minmax(0, 1fr)",
    gap: 24,
    alignItems: "start",
    padding: "12px 0",
    "&:not(:last-child)": {
      borderBottom: "1px solid #eeeeee"
    },
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
      gap: 4
    }
  },
  remarkLabel: {
    color: "#616161",
    fontSize: 13
  },
  remarkValue: {
    color: "#212121",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
    overflowWrap: "anywhere",
    "@media (max-width: 600px)": {
      textAlign: "left"
    }
  },
  emptyRemarks: {
    minHeight: 64,
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    color: "#757575",
    backgroundColor: "#fafafa"
  },
  emptyRemarksIcon: {
    marginRight: 10,
    color: "#9e9e9e",
    fontSize: 20
  }
});

const renderTransactionStatus = value => {
  if (isEmpty(value)) return "-";

  const backgroundColor = value === "Return Confirmed" ? colorLightGreen.dark : value === "Missing" ? colorTomatoRed.dark : colorSecondary.dark;

  return (
    <span
      style={{
        backgroundColor,
        color: "white",
        padding: "4px 8px",
        borderRadius: 4,
        whiteSpace: "nowrap"
      }}>
      {value}
    </span>
  );
};

const OrderClosureView = props => {
  const classes = useStyles();
  const { id, roles, hasAccess, translate } = props;

  const [selectedKey, setSelectedKey] = React.useState(null);
  const [version, setVersion] = React.useState(0);
  const [drawerState, setDrawerState] = useSafeState({ open: false, action: null, selectedRolls: [] });
  const [detailsRoll, setDetailsRoll] = useSafeState(null);

  const { allUnitList } = useUnit();
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const minReturnRollLengthStr = useSetting(SettingKeys.MIN_ROLL_RETURN_LENGTH);
  const higherRecutThresholdPercent = useSetting(SettingKeys.RECUTTING_THRESHOLD_PERCENT);
  const actualEndBitsThresholdPercent = useSetting(SettingKeys.HIGHER_END_BITS_THRESHOLD_PERCENT);

  const minReturnRollLength = Number(minReturnRollLengthStr) || 0;
  const { utc_offset } = useTime();
  const dispatch = useDispatch();
  const [mutate, { loading: busy }] = useMutation();

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" }, version });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList)) return [];
    return getCutOrderRows(order, productParts).map(row => ({
      ...row,
      poRef: order?.poRef,
      customerName: order.customer?.name,
      seasonName: order.season?.name
    }));
  }, [order, productParts]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: value => value || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "fpoQty", title: "FPO Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      // { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }

    return cols;
  }, [flowInfoRows, allUnitList]);

  useDeepEffect(() => {
    if (!isEmpty(flowInfoRows)) {
      setSelectedKey(flowInfoRows[0].key);
    }
  }, [flowInfoRows.length]);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
    }
  };

  const orderFlow = flowInfoRows.find(fi => fi.key === selectedKey);
  const cutOrder = orderFlow?.cutOrder || {};

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(cutOrder) && {
      url: "rolls",
      params: { search: `cutOrderFlow.id==${cutOrder.id};(hasChild=='NULL',hasChild=='FALSE');(active=in=('TRUE', 'FALSE'))`, size: 100000 },
      version
    }
  );

  const { data: rollReturnList = [] } = useQuery(!isEmpty(cutOrder) && { url: "roll-return", params: { cutOrderId: cutOrder.id }, version });

  const refresh = () => setVersion(v => v + 1);

  const fabricReleased = rollInfoList.some(r => !isEmpty(r.fabricReleaseId));
  const cutOrderClosed = cutOrder?.stateValue === OState.CLOSED.stateValue;
  const isKnitwear = rollInfoList.some(isKnitRoll);

  const isSuperUser = Array.isArray(roles) && (roles.includes(Role.MASTER) || roles.includes(Role.SUPER_USER));
  const isFabricStore = Array.isArray(roles) && roles.includes(Role.FABRIC_STORE_MANAGEMENT);
  const isCuttingIncharge = Array.isArray(roles) && roles.includes(Role.CUTTING_IN_CHARGE);
  const isCuttingPlanner = Array.isArray(roles) && roles.includes(Role.CUT_PLANNER);

  const canWrite = hasPrivilege(roles, hasAccess, ResourceId.ROLL_RETURN, Scope.WRITE);
  const canConfirm = hasPrivilege(roles, hasAccess, ResourceId.ROLL_RETURN, Scope.UPDATE);
  const canDelete = hasPrivilege(roles, hasAccess, ResourceId.ROLL_RETURN, Scope.DELETE);

  const hasOrderClosureAccess = hasPrivilege(roles, hasAccess, ResourceId.CLOSE_CUT_ORDER, Scope.WRITE);

  const isCuttingSide = (isSuperUser || isCuttingIncharge || isCuttingPlanner) && canWrite;
  const isWarehouseSide = isSuperUser || isFabricStore;

  //** Rolls not yet part of any roll-return transaction, eligible for selection **//
  const selectableRollList = useDeepMemo(() => rollInfoList.filter(r => isEmpty(r.rollReturnId)), [rollInfoList]);

  const availableRows = useDeepMemo(
    () => selectableRollList.filter(r => r.available >= minReturnRollLength).map(({ parentId, ...e }) => e),
    [selectableRollList, minReturnRollLength]
  );

  const unusedRows = useDeepMemo(
    () => selectableRollList.filter(r => r.unused > 0 && r.available < minReturnRollLength).map(({ parentId, ...e }) => e),
    [selectableRollList, minReturnRollLength, isKnitwear]
  );

  //** Roll Transaction History rows: rolls already part of a roll-return transaction **//
  const transactionRows = useDeepMemo(() => {
    let txnRows = buildTransactionRows(rollInfoList, rollReturnList);
    if (!isCuttingSide && isWarehouseSide) {
      txnRows = txnRows.filter(r => r.txnType === RollReturnTxnType.RETURN);
    }
    return txnRows.sort((a, b) => b.rollReturnId - a.rollReturnId);
  }, [rollInfoList, rollReturnList, isCuttingSide, isWarehouseSide]);

  const rollColumns = useDeepMemo(
    () => [
      { dataKey: "rollNo", title: "F. Roll No." },
      { dataKey: "supplierRollNo", title: "S. Roll No." },
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "invStatus", title: "Inv. Status" },
      { dataKey: "rollType", title: "Roll Type", filterOptions: { filter: true } },
      { dataKey: "shade", title: "Shade", filterOptions: { filter: true } },
      { dataKey: "width", title: "Width", filterOptions: { filter: true }, render: value => (isEmpty(value) ? "-" : `${value} cm`) },
      isKnitwear
        ? { dataKey: "weight", title: "Weight", render: value => `${toFixedValue(value, 3)} kg` }
        : { dataKey: "length", title: "Length", render: value => `${toFixedValue(value)} m` },
      isKnitwear
        ? { dataKey: "availableWeight", title: "Available Weight", render: value => `${toFixedValue(value, 3)} kg` }
        : { dataKey: "available", title: "Available Length", render: value => `${toFixedValue(value)} m` }
    ],
    [isKnitwear]
  );

  const selectActions = useDeepMemo(
    () => [
      { name: "return", tooltip: "RETURN", showLabel: true, options: { variant: "outlined", color: "primary", style: { marginLeft: "1em" } } },
      { name: "missing", tooltip: "MISSING", showLabel: true, options: { variant: "outlined", color: "primary", style: { marginLeft: "1em" } } }
    ],
    []
  );

  const titleSelected = selectedRows => {
    const selectedQty = selectedRows.reduce((acc, e) => acc + (isKnitwear ? e.weight || 0 : e.available || 0), 0);
    return `${selectedRows?.length} Roll(s): ${toFixed(selectedQty)} ${isKnitwear ? "kg" : "m"} selected`;
  };

  const transactionTitleSelected = selectedRows => {
    const selectedQty = selectedRows.reduce((acc, e) => acc + (Number(e.txnQty) || 0), 0);
    return `${selectedRows?.length} Roll(s): ${toFixed(selectedQty)} ${isKnitwear ? "kg" : "m"} selected`;
  };

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (!isEmpty(selectedRows)) {
      if (action === "return") {
        setDrawerState({ open: true, action: RollReturnTxnType.RETURN, selectedRolls: selectedRows });
      } else if (action === "missing") {
        setDrawerState({ open: true, action: RollReturnTxnType.MISSING, selectedRolls: selectedRows });
      }
    }
    onActionComplete && onActionComplete(selectedRows);
  };

  const transactionColumns = useDeepMemo(
    () => [
      { dataKey: "rollReturnId", title: "TXN ID" },
      { dataKey: "rollNo", title: "F. Roll No." },
      { dataKey: "supplierRollNo", title: "S. Roll No." },
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "rollType", title: "Roll Type", filterOptions: { filter: true } },
      { dataKey: "shade", title: "Shade", filterOptions: { filter: true } },
      { dataKey: "width", title: "Width", filterOptions: { filter: true }, render: value => (isEmpty(value) ? "-" : `${value} cm`) },
      isKnitwear
        ? { dataKey: "txnQty", title: "Transaction Qty", render: value => `${toFixedValue(value, 3)} kg` }
        : { dataKey: "txnQty", title: "Transaction Qty", render: value => `${toFixedValue(value)} m` },
      { dataKey: "txnTime", title: "Transaction Time", render: value => (isEmpty(value) ? "-" : formatDateTime(value, utc_offset)) },
      {
        dataKey: "txnStatusLabel",
        title: "Transaction Status",
        filterOptions: { filter: true },
        render: renderTransactionStatus
      },
      {
        dataKey: "actions",
        title: "",
        render: (value, _, row) => (
          <IconButton size="small" onClick={() => setDetailsRoll(row)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        )
      }
    ],
    [isKnitwear, utc_offset]
  );

  const confirmSelectActions = useDeepMemo(
    () => [{ name: "confirm", tooltip: "CONFIRM ROLL RETURN", showLabel: true, options: { variant: "contained", color: "primary" } }],
    []
  );

  const onTxnSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (!isEmpty(selectedRows) && action === "confirm") {
      setDrawerState({ open: true, action: "confirm", selectedRolls: selectedRows });
    }
    onActionComplete && onActionComplete(selectedRows);
  };

  const onClose = () => {
    if (!isEmpty(availableRows)) {
      dispatch(showNotification(`Order cannot be closed until available rolls are returned to Warehouse.`, { type: "danger" }));
      return;
    }

    const rollList = Array.isArray(rollInfoList) ? rollInfoList : [];
    const usedInPanel = rollList?.reduce((acc, e) => acc + (e.usedInPanel || 0), 0);
    const actualEndBits = rollList?.reduce((acc, e) => acc + (e.actualEndBits || 0), 0);
    const hasWeight = rollList.some(e => !isEmpty(e.weight) && e.weight > 0);

    const allocated = hasWeight ? cutOrder?.allocatedWeight || 0 : cutOrder?.allocatedQty || 0;
    const usedInPanelPercent = allocated > 0 ? (usedInPanel * 100) / allocated : 0;
    const actualEndBitsPercent = allocated > 0 ? (actualEndBits * 100) / allocated : 0;

    const hasFabricLeftover = !isEmpty(rollReturnList) && rollReturnList.some(e => e.type === RollReturnTxnType.RETURN);

    if (hasFabricLeftover || usedInPanelPercent > higherRecutThresholdPercent || actualEndBitsPercent > actualEndBitsThresholdPercent) {
      setDrawerState({ open: true, action: "close" });
    } else {
      closeOrder({});
    }
  };

  const closeOrder = values => {
    const rollList = Array.isArray(rollInfoList) ? rollInfoList : [];
    const usedInPanel = rollList?.reduce((acc, e) => acc + (e.usedInPanel || 0), 0);
    const actualEndBits = rollList?.reduce((acc, e) => acc + (e.actualEndBits || 0), 0);
    const hasWeight = rollList.some(e => !isEmpty(e.weight) && e.weight > 0);

    const allocated = hasWeight ? cutOrder?.allocatedWeight || 0 : cutOrder?.allocatedQty || 0;
    const usedInPanelPercent = allocated > 0 ? (usedInPanel * 100) / allocated : 0;
    const actualEndBitsPercent = allocated > 0 ? (actualEndBits * 100) / allocated : 0;

    const hasFabricLeftover = !isEmpty(rollReturnList) && rollReturnList.some(e => e.type === RollReturnTxnType.RETURN);

    if (hasFabricLeftover) {
      if (isEmpty(values["fabricLeftover"])) {
        dispatch(showNotification(`Fabric Leftover Reason is required`, { type: "danger" }));
        return;
      }
    }
    if (usedInPanelPercent > higherRecutThresholdPercent) {
      if (isEmpty(values["higherRecut"])) {
        dispatch(showNotification(`Higher Recut Reason is required`, { type: "danger" }));
        return;
      }
    }

    if (actualEndBitsPercent > actualEndBitsThresholdPercent) {
      if (isEmpty(values["higherEndBits"])) {
        dispatch(showNotification(`Higher End Bits Leftover Reason is required`, { type: "danger" }));
        return;
      }
    }

    const options = {
      url: `reports/cut-order/close`,
      method: "post",
      params: { orderId: id, cutOrderId: cutOrder?.id },
      data: {
        alertReasons: JSON.stringify(
          Object.entries(values)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => ({ parameter: key, reasons: Array.isArray(value) ? value : [value] }))
        )
      }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Order closed successfully`, { type: "success" }));
        setDrawerState({ open: false });
        setVersion(v => v + 1);
      }
    });
  };

  const openOrder = values => {
    const options = {
      url: `reports/cut-order/open`,
      method: "post",
      params: { orderId: id, cutOrderId: cutOrder?.id },
      data: {}
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Order opened successfully`, { type: "success" }));
        setVersion(v => v + 1);
      }
    });
  };

  const detailsCanDelete =
    !isEmpty(detailsRoll) &&
    canDelete &&
    !cutOrderClosed &&
    isCuttingSide &&
    (detailsRoll.txnType === RollReturnTxnType.MISSING || detailsRoll.returnStatus !== ReturnStatus.CONFIRMED);

  const detailsCanDeleteConfirmation =
    !isEmpty(detailsRoll) &&
    canDelete &&
    !cutOrderClosed &&
    isWarehouseSide &&
    detailsRoll.txnType === RollReturnTxnType.RETURN &&
    detailsRoll.returnStatus === ReturnStatus.CONFIRMED;

  const remarkList = useDeepMemo(() => {
    if (isEmpty(cutOrder) || cutOrder.state !== OState.CLOSED.state) return [];
    const alerts = !isEmpty(cutOrder.orderConfig?.alerts) ? JSON.parse(cutOrder.orderConfig?.alerts) : [];
    return alerts
      .filter(alert => Object.keys(ALERT_PARAM_TITLE).includes(alert.parameter))
      .map(alert => ({ label: ALERT_PARAM_TITLE[alert.parameter], value: Array.isArray(alert.reasons) ? alert.reasons.join(", ") : alert.reasons }));
  }, [cutOrder]);

  return (
    <div>
      <PageHeader title="Order Closure" />

      <OrderCloseDrawer
        open={drawerState.open && drawerState.action === "close"}
        onRefresh={refresh}
        onDrawerClose={() => setDrawerState({ open: false, action: null, selectedRolls: [] })}
        onOrderClose={closeOrder}
      />

      <RollReturnDrawer
        open={drawerState.open && [RollReturnTxnType.RETURN, RollReturnTxnType.MISSING].includes(drawerState.action)}
        txnType={drawerState.action}
        cutOrderId={cutOrder.id}
        selectedRolls={drawerState.selectedRolls}
        onRefresh={refresh}
        onDrawerClose={() => setDrawerState({ open: false, action: null, selectedRolls: [] })}
      />

      <RollReturnConfirmDrawer
        open={drawerState.open && drawerState.action === "confirm"}
        cutOrderId={cutOrder.id}
        selectedRolls={drawerState.selectedRolls}
        onRefresh={refresh}
        onDrawerClose={() => setDrawerState({ open: false, action: null, selectedRolls: [] })}
      />

      <RollReturnDetailsDrawer
        open={!isEmpty(detailsRoll)}
        cutOrderId={cutOrder.id}
        roll={detailsRoll || {}}
        rollList={rollInfoList}
        canDelete={detailsCanDelete}
        canDeleteConfirmation={detailsCanDeleteConfirmation}
        onRefresh={refresh}
        onDrawerClose={() => setDetailsRoll(null)}
      />

      <div className={classes.content}>
        <div>
          <MuiTable
            title={translate("resources.rollPlan.title.cut_order", { _: "Cut Order" })}
            toolbar={true}
            searchable={true}
            pageable={true}
            searchKeys={["flowRef", "customerFlowRef", "style", "styleNo"]}
            idKey="key"
            parentIdKey="parentKey"
            columns={flowInfoColumns}
            rows={flowInfoRows}
            defaultExpanded={false}
            expandedColor={["#BFDDF8", "#7AB9F0"]}
            onRowClick={onRowClick}
            rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
            i18nKey="resources.grouping.fields.flowInfo"
          />
        </div>

        {(!cutOrder || !fabricReleased) && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">Fabric not released yet. Fabric return or order closure will be available after fabric is released.</Typography>
          </Alert>
        )}

        {cutOrder && fabricReleased && (
          <div>
            <div className={classes.mrTop}>
              <MuiTable
                key={`txn-${version}`}
                title="Roll Transaction History"
                toolbar={true}
                searchable={true}
                pageable={true}
                searchKeys={["rollNo", "supplierRollNo"]}
                idKey="rollReturnId"
                columns={transactionColumns}
                rows={transactionRows}
                selectable={isWarehouseSide ? row => row.txnStatusLabel === "Return Initiated" : undefined}
                selectAll={false}
                titleSelected={transactionTitleSelected}
                selectActions={isWarehouseSide && canConfirm ? confirmSelectActions : undefined}
                onSelectActionClick={onTxnSelectActionClick}
              />
            </div>

            {isCuttingSide && (
              <div className={classes.mrTop}>
                <MuiTable
                  key={`unused-${version}`}
                  title={
                    <Tooltip
                      title="Greater than Lowest Marker Length and Less than returnable roll length"
                      classes={{ tooltip: classes.rollDefinitionTooltip }}
                      arrow>
                      <span className={classes.tableTitle}>
                        Unused Rolls
                        <InfoOutlinedIcon className={classes.tableTitleInfo} aria-label="Unused Rolls definition" />
                      </span>
                    </Tooltip>
                  }
                  toolbar={true}
                  searchable={true}
                  pageable={true}
                  searchKeys={["rollNo", "supplierRollNo"]}
                  idKey="id"
                  columns={rollColumns}
                  rows={unusedRows}
                  selectable
                  titleSelected={titleSelected}
                  selectActions={selectActions}
                  onSelectActionClick={onSelectActionClick}
                />
              </div>
            )}

            {isCuttingSide && !cutOrderClosed && (
              <div className={classes.mrTop}>
                <MuiTable
                  key={`available-${version}`}
                  title={
                    <Tooltip
                      title="Roll whose length is greater than min returnable length"
                      classes={{ tooltip: classes.rollDefinitionTooltip }}
                      arrow>
                      <span className={classes.tableTitle}>
                        Available Rolls
                        <InfoOutlinedIcon className={classes.tableTitleInfo} aria-label="Available Rolls definition" />
                      </span>
                    </Tooltip>
                  }
                  toolbar={true}
                  searchable={true}
                  pageable={true}
                  searchKeys={["rollNo", "supplierRollNo"]}
                  idKey="id"
                  columns={rollColumns}
                  rows={availableRows}
                  selectable
                  titleSelected={titleSelected}
                  selectActions={selectActions}
                  onSelectActionClick={onSelectActionClick}
                />
              </div>
            )}

            {cutOrderClosed && (
              <Card className={classes.mrTop}>
                <CardHeader
                  className={classes.remarksHeader}
                  title="Order Closure Remarks"
                  titleTypographyProps={{ variant: "subtitle1", className: classes.remarksTitle }}
                />
                <Divider />
                {isEmpty(remarkList) ? (
                  <div className={classes.emptyRemarks}>
                    <InfoOutlinedIcon className={classes.emptyRemarksIcon} />
                    <Typography variant="body2" color="textSecondary">
                      No remarks were added when this order was closed.
                    </Typography>
                  </div>
                ) : (
                  <div className={classes.remarksContent}>
                    {remarkList.map(({ label, value }) => (
                      <div key={label} className={classes.remarkRow}>
                        <Typography component="span" className={classes.remarkLabel}>
                          {label}
                        </Typography>
                        <Typography component="span" className={classes.remarkValue}>
                          {value}
                        </Typography>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {(cutOrder?.stateValue === OState.REMNANT_FEEDBACK_COMPLETED.stateValue ||
              cutOrder?.stateValue === OState.FEEDBACK_COMPLETED.stateValue) &&
              hasOrderClosureAccess && (
                <div className={classes.footer}>
                  <Button label={"Close Order"} variant="contained" color="primary" disabled={busy} onClick={onClose}>
                    <CheckIcon />
                  </Button>
                </div>
              )}

            {cutOrder?.stateValue === OState.CLOSED.stateValue && isSuperUser && (
              <div className={classes.footer}>
                <Button label={"Open Order"} variant="contained" color="primary" disabled={busy} onClick={openOrder}>
                  <CheckIcon />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default translate(OrderClosureView);
